import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// API SERVICE (inline)
// ─────────────────────────────────────────────────────────────

const API_URL = "https://scanmymenu-nm38.onrender.com";
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 30000;

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: { "Content-Type": "application/json" },
});

axiosInstance.interceptors.response.use(
  (r) => r.data,
  (err) => Promise.reject(new Error(err.response?.data?.message || err.message || "An error occurred"))
);

const axiosFormInstance = axios.create({ baseURL: API_URL, timeout: API_TIMEOUT });

axiosFormInstance.interceptors.response.use(
  (r) => r.data,
  (err) => Promise.reject(new Error(err.response?.data?.message || err.message || "An error occurred"))
);

const apiService = {
  createRestaurant: (formData) =>
    axiosFormInstance.post("/api/restaurants", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getAllRestaurants: () => axiosInstance.get("/api/restaurants"),
  getRestaurantDetails: (id) => axiosInstance.get(`/api/restaurants/${id}`),
  getRestaurantByUserId: (id) => axiosInstance.get(`/api/restaurants/id/${id}`),
  updateRestaurant: (id, data) => axiosInstance.put(`/api/restaurants/${id}`, data),
  deleteRestaurant: (id) => axiosInstance.delete(`/api/restaurants/${id}`),
  getRestaurantItems: (id) => axiosInstance.get(`/api/restaurants/${id}/items`),
  addRestaurantItem: (id, formData) =>
    axiosFormInstance.post(`/api/restaurants/${id}/items`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  updateRestaurantItem: (restaurantId, itemId, formData) =>
    axiosFormInstance.put(`/api/restaurants/${restaurantId}/items/${itemId}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteRestaurantItem: (restaurantId, itemId) =>
    axiosInstance.delete(`/api/restaurants/${restaurantId}/items/${itemId}`),
  getItemDetails: (itemId) => axiosInstance.get(`/api/restaurants/items/${itemId}`),
  getAllItems: () => axiosInstance.get("/api/restaurants/items"),
  submitContact: (data) => axiosInstance.post("/api/contact", data),

  placeOrder: async ({ restaurantId, tableNumber, cart }) => {
    const res = await fetch(`${API_URL}/api/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantId, tableNumber, items: cart }),
    });
    return res.json();
  },

  getOrders: async (restaurantId, filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const res = await fetch(`${API_URL}/api/orders/${restaurantId}${params ? "?" + params : ""}`);
    return res.json();
  },

  updateOrderStatus: async (orderId, status) => {
    const res = await fetch(`${API_URL}/api/orders/${orderId}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    return res.json();
  },
};

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────

const STATUS = {
  pending:   { label: "Pending",   emoji: "🕐", color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.3)", next: "preparing", dot: "#f59e0b" },
  preparing: { label: "Preparing", emoji: "👨‍🍳", color: "#60a5fa", bg: "rgba(96,165,250,0.12)", border: "rgba(96,165,250,0.3)", next: "ready",     dot: "#60a5fa" },
  ready:     { label: "Ready",     emoji: "✅", color: "#34d399", bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.3)", next: "served",    dot: "#34d399" },
  served:    { label: "Served",    emoji: "🍽️", color: "#94a3b8", bg: "rgba(148,163,184,0.1)",  border: "rgba(148,163,184,0.2)", next: null,       dot: "#94a3b8" },
  cancelled: { label: "Cancelled", emoji: "❌", color: "#f87171", bg: "rgba(248,113,113,0.1)",  border: "rgba(248,113,113,0.2)", next: null,       dot: "#f87171" },
};

const TABS = ["all", "pending", "preparing", "ready", "served"];

// ─────────────────────────────────────────────────────────────
// INJECTED STYLES
// ─────────────────────────────────────────────────────────────

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: #0a0c10;
    color: #e2e8f0;
    font-family: 'Syne', sans-serif;
    min-height: 100vh;
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #2d3748; border-radius: 4px; }

  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.4); }
    50%       { box-shadow: 0 0 0 8px rgba(245,158,11,0); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes livePulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.5; transform: scale(0.85); }
  }
  @keyframes newOrderPop {
    0%   { transform: scale(0.92); opacity: 0; }
    60%  { transform: scale(1.02); }
    100% { transform: scale(1);    opacity: 1; }
  }
  @keyframes countUp {
    from { transform: translateY(8px); opacity: 0; }
    to   { transform: translateY(0);   opacity: 1; }
  }

  .od-page {
    max-width: 780px;
    margin: 0 auto;
    padding: 28px 20px 100px;
    font-family: 'Syne', sans-serif;
  }

  /* ── TOP BAR ── */
  .od-topbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 32px;
    gap: 16px;
  }
  .od-brand {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .od-brand-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #f59e0b, #ef4444);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
    box-shadow: 0 8px 24px rgba(245,158,11,0.25);
  }
  .od-title {
    font-size: 24px;
    font-weight: 800;
    color: #f1f5f9;
    letter-spacing: -0.5px;
    line-height: 1;
  }
  .od-subtitle {
    margin-top: 5px;
    font-size: 12px;
    color: #64748b;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .od-live-dot {
    width: 7px;
    height: 7px;
    background: #34d399;
    border-radius: 50%;
    animation: livePulse 1.4s ease-in-out infinite;
    display: inline-block;
  }
  .od-topbar-right {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-shrink: 0;
  }
  .od-refresh-btn {
    background: #161b27;
    border: 1px solid #1e2d40;
    border-radius: 12px;
    padding: 10px 18px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    color: #94a3b8;
    font-family: 'Syne', sans-serif;
    transition: all 0.18s ease;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .od-refresh-btn:hover {
    background: #1e2d40;
    color: #e2e8f0;
    border-color: #2d4460;
  }
  .od-refresh-btn svg {
    transition: transform 0.4s ease;
  }
  .od-refresh-btn:hover svg {
    transform: rotate(180deg);
  }

  /* ── STATS ── */
  .od-stats {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    margin-bottom: 28px;
  }
  @media (max-width: 600px) {
    .od-stats { grid-template-columns: repeat(3, 1fr); }
  }
  .od-stat-card {
    background: #0f1420;
    border: 1px solid #1a2235;
    border-radius: 16px;
    padding: 16px 12px 14px;
    text-align: center;
    animation: fadeSlideIn 0.4s ease both;
    transition: border-color 0.2s, transform 0.2s;
    cursor: default;
  }
  .od-stat-card:hover {
    border-color: #2d3f5c;
    transform: translateY(-2px);
  }
  .od-stat-value {
    font-size: 26px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -1px;
    animation: countUp 0.35s ease both;
  }
  .od-stat-label {
    margin-top: 6px;
    font-size: 10px;
    color: #475569;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  /* ── TABS ── */
  .od-tabs {
    display: flex;
    gap: 6px;
    margin-bottom: 20px;
    overflow-x: auto;
    padding-bottom: 2px;
  }
  .od-tab {
    flex-shrink: 0;
    background: transparent;
    border: 1px solid #1a2235;
    border-radius: 10px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    cursor: pointer;
    font-family: 'Syne', sans-serif;
    transition: all 0.18s ease;
    display: flex;
    align-items: center;
    gap: 7px;
    position: relative;
  }
  .od-tab:hover {
    border-color: #2d3f5c;
    color: #94a3b8;
    background: #0f1420;
  }
  .od-tab-active {
    background: #f59e0b !important;
    border-color: #f59e0b !important;
    color: #0a0c10 !important;
  }
  .od-tab-badge {
    border-radius: 6px;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    min-width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    font-family: 'JetBrains Mono', monospace;
  }

  /* ── EMPTY / LOADING ── */
  .od-empty {
    text-align: center;
    padding: 80px 20px;
    color: #334155;
  }
  .od-empty-icon { font-size: 48px; margin-bottom: 12px; }
  .od-empty-text { font-size: 15px; font-weight: 600; }

  .od-skeleton {
    background: linear-gradient(90deg, #0f1420 25%, #161b27 50%, #0f1420 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 10px;
  }

  /* ── ORDER LIST ── */
  .od-order-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* ── ORDER CARD ── */
  .od-card {
    background: #0f1420;
    border: 1px solid #1a2235;
    border-radius: 20px;
    padding: 18px;
    position: relative;
    overflow: hidden;
    animation: fadeSlideIn 0.35s ease both;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .od-card:hover {
    border-color: #2d3f5c;
    box-shadow: 0 4px 32px rgba(0,0,0,0.3);
  }
  .od-card-new {
    border-color: rgba(245,158,11,0.5) !important;
    box-shadow: 0 0 0 3px rgba(245,158,11,0.08), 0 8px 40px rgba(245,158,11,0.1) !important;
    animation: newOrderPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both !important;
  }
  .od-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.015) 0%, transparent 60%);
    pointer-events: none;
    border-radius: inherit;
  }

  /* Card Header */
  .od-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
    gap: 12px;
  }
  .od-card-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .od-table-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #161b27;
    border: 1px solid #1e2d40;
    border-radius: 10px;
    padding: 6px 14px;
    font-size: 14px;
    font-weight: 800;
    color: #e2e8f0;
    letter-spacing: -0.3px;
  }
  .od-order-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #334155;
    font-weight: 500;
  }
  .od-status-chip {
    border-radius: 10px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .od-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    animation: livePulse 1.8s ease infinite;
  }

  /* Divider */
  .od-divider {
    height: 1px;
    background: #1a2235;
    margin: 14px 0;
  }

  /* Items */
  .od-items-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 14px;
  }
  .od-item-row {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #0a0c10;
    border: 1px solid #141920;
    border-radius: 12px;
    padding: 9px 12px;
    transition: border-color 0.15s;
  }
  .od-item-row:hover { border-color: #1e2d40; }
  .od-item-thumb {
    width: 44px;
    height: 44px;
    border-radius: 9px;
    object-fit: cover;
    flex-shrink: 0;
    background: #161b27;
  }
  .od-item-info { flex: 1; min-width: 0; }
  .od-item-name {
    font-size: 14px;
    font-weight: 700;
    color: #e2e8f0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .od-item-qty {
    margin-top: 3px;
    font-size: 12px;
    color: #475569;
    font-family: 'JetBrains Mono', monospace;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .od-qty-badge {
    background: #161b27;
    border: 1px solid #1a2235;
    border-radius: 6px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 700;
    color: #f59e0b;
  }
  .od-item-subtotal {
    font-size: 14px;
    font-weight: 800;
    color: #f1f5f9;
    font-family: 'JetBrains Mono', monospace;
    flex-shrink: 0;
  }

  /* Card Footer */
  .od-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  .od-total-text {
    font-size: 15px;
    color: #64748b;
    display: flex;
    align-items: baseline;
    gap: 6px;
  }
  .od-total-amount {
    font-size: 18px;
    font-weight: 800;
    color: #f1f5f9;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: -0.5px;
  }
  .od-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .od-cancel-btn {
    border: 1px solid rgba(248,113,113,0.25);
    background: rgba(248,113,113,0.08);
    color: #f87171;
    border-radius: 10px;
    padding: 9px 15px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Syne', sans-serif;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .od-cancel-btn:hover {
    background: rgba(248,113,113,0.15);
    border-color: rgba(248,113,113,0.4);
  }
  .od-cancel-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .od-next-btn {
    border: none;
    border-radius: 10px;
    padding: 9px 18px;
    font-size: 13px;
    font-weight: 800;
    color: #0a0c10;
    cursor: pointer;
    font-family: 'Syne', sans-serif;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    gap: 7px;
    letter-spacing: -0.2px;
  }
  .od-next-btn:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .od-next-btn:active { transform: translateY(0); }
  .od-next-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  /* New badge */
  .od-new-badge {
    position: absolute;
    top: 0;
    right: 0;
    background: linear-gradient(135deg, #f59e0b, #ef4444);
    color: #0a0c10;
    border-radius: 0 18px 0 14px;
    padding: 5px 14px;
    font-size: 10px;
    font-weight: 800;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    animation: pulseGlow 1.5s infinite;
  }

  /* Skeleton loading cards */
  .od-skel-card {
    background: #0f1420;
    border: 1px solid #1a2235;
    border-radius: 20px;
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: fadeSlideIn 0.3s ease both;
  }
  .od-skel-line { height: 14px; }
  .od-skel-block { height: 60px; }
`;

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────

export default function OwnerDashboard() {
  const { restaurantId } = useParams();

  const [orders,      setOrders]      = useState([]);
  const [stats,       setStats]       = useState({ total: 0, pending: 0, preparing: 0, ready: 0, served: 0, revenue: 0 });
  const [activeTab,   setActiveTab]   = useState("all");
  const [loading,     setLoading]     = useState(true);
  const [updatingId,  setUpdatingId]  = useState(null);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [refreshSpin, setRefreshSpin] = useState(false);
  const sseRef = useRef(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const fetchOrders = useCallback(async () => {
    setRefreshSpin(true);
    try {
      const data = await apiService.getOrders(restaurantId);
      if (data.success) {
        setOrders(data.orders);
        setStats(data.stats);
      }
    } catch (e) {
      console.error("fetchOrders:", e);
    } finally {
      setLoading(false);
      setTimeout(() => setRefreshSpin(false), 400);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchOrders();

    const url = `${API_URL}/api/orders/live/${restaurantId}`;
    const es  = new EventSource(url);
    sseRef.current = es;

    es.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);

        if (payload.event === "new_order") {
          const o = payload.order;
          setOrders((prev) => prev.find((x) => x.orderId === o.orderId) ? prev : [o, ...prev]);
          setStats((s) => ({ ...s, total: s.total + 1, pending: s.pending + 1 }));
          setNewOrderIds((prev) => new Set(prev).add(o.orderId));
          setTimeout(() => {
            setNewOrderIds((prev) => { const n = new Set(prev); n.delete(o.orderId); return n; });
          }, 5000);
          if (Notification.permission === "granted") {
            new Notification(`🛎️ New Order — Table ${o.tableNumber}`, {
              body: `${o.items.length} item(s) · ₹${o.totalPrice}`,
            });
          }
        }

        if (payload.event === "status_update") {
          setOrders((prev) =>
            prev.map((o) => o.orderId === payload.orderId ? { ...o, status: payload.status } : o)
          );
        }
      } catch (_) {}
    };

    es.onerror = () => es.close();
    if (Notification.permission === "default") Notification.requestPermission();
    return () => es.close();
  }, [restaurantId, fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await apiService.updateOrderStatus(orderId, newStatus);
      setOrders((prev) =>
        prev.map((o) => o.orderId === orderId ? { ...o, status: newStatus } : o)
      );
      setStats((s) => {
        const order = orders.find((o) => o.orderId === orderId);
        if (!order) return s;
        return {
          ...s,
          [order.status]: Math.max(0, (s[order.status] || 0) - 1),
          [newStatus]: (s[newStatus] || 0) + 1,
          revenue:
            newStatus === "served" ? s.revenue + order.totalPrice
            : order.status === "served" ? s.revenue - order.totalPrice
            : s.revenue,
        };
      });
    } catch (e) {
      console.error("updateStatus:", e);
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = activeTab === "all" ? orders : orders.filter((o) => o.status === activeTab);

  const fmtTime = (ts) => {
    if (!ts) return "";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  const STAT_CONFIG = [
    { label: "Total",    value: stats.total,                            color: "#e2e8f0", delay: "0s"    },
    { label: "Pending",  value: stats.pending,                          color: "#f59e0b", delay: "0.05s" },
    { label: "Cooking",  value: stats.preparing,                        color: "#60a5fa", delay: "0.1s"  },
    { label: "Ready",    value: stats.ready,                            color: "#34d399", delay: "0.15s" },
    { label: "Revenue",  value: `₹${(stats.revenue || 0).toFixed(0)}`,  color: "#a78bfa", delay: "0.2s"  },
  ];

  return (
    <div className="od-page">

      {/* TOP BAR */}
      <div className="od-topbar">
        <div className="od-brand">
          <div className="od-brand-icon">🍳</div>
          <div>
            <h1 className="od-title">Order Command</h1>
            <p className="od-subtitle">
              <span className="od-live-dot" />
              Live kitchen dashboard
            </p>
          </div>
        </div>
        <div className="od-topbar-right">
          <button className="od-refresh-btn" onClick={fetchOrders}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{ transform: refreshSpin ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.4s ease" }}>
              <path d="M23 4v6h-6M1 20v-6h6"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="od-stats">
        {STAT_CONFIG.map(({ label, value, color, delay }) => (
          <div key={label} className="od-stat-card" style={{ animationDelay: delay }}>
            <p className="od-stat-value" style={{ color }}>{value}</p>
            <p className="od-stat-label">{label}</p>
          </div>
        ))}
      </div>

      {/* TABS */}
      <div className="od-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`od-tab${activeTab === tab ? " od-tab-active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "all" ? "All Orders" : `${STATUS[tab]?.emoji} ${STATUS[tab]?.label}`}
            {tab !== "all" && stats[tab] > 0 && (
              <span className="od-tab-badge" style={{ background: STATUS[tab]?.color }}>
                {stats[tab]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ORDER LIST */}
      {loading ? (
        <div className="od-order-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="od-skel-card" style={{ animationDelay: `${i * 0.07}s` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="od-skeleton od-skel-line" style={{ width: 120 }} />
                <div className="od-skeleton od-skel-line" style={{ width: 80 }} />
              </div>
              <div className="od-skeleton od-skel-block" style={{ borderRadius: 12 }} />
              <div className="od-skeleton od-skel-line" style={{ width: "60%" }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="od-empty">
          <div className="od-empty-icon">📭</div>
          <p className="od-empty-text">No orders in this category</p>
        </div>
      ) : (
        <div className="od-order-list">
          {filtered.map((order, idx) => {
            const st    = STATUS[order.status] || STATUS.pending;
            const isNew = newOrderIds.has(order.orderId);
            const busy  = updatingId === order.orderId;

            return (
              <div
                key={order.orderId}
                className={`od-card${isNew ? " od-card-new" : ""}`}
                style={{ animationDelay: `${idx * 0.04}s` }}
              >
                {/* Header */}
                <div className="od-card-header">
                  <div className="od-card-left">
                    <div className="od-table-chip">
                      🪑 Table {order.tableNumber}
                    </div>
                    <span className="od-order-time">{fmtTime(order.createdAt)}</span>
                  </div>
                  <div
                    className="od-status-chip"
                    style={{ background: st.bg, color: st.color, borderColor: st.border }}
                  >
                    <span className="od-status-dot" style={{ background: st.dot }} />
                    {st.label}
                  </div>
                </div>

                {/* Items */}
                <div className="od-items-list">
                  {(order.items || []).map((item, idx2) => (
                    <div key={idx2} className="od-item-row">
                      <img
                        src={item.imageUrl || "https://via.placeholder.com/44"}
                        alt={item.name}
                        className="od-item-thumb"
                        onError={(e) => { e.target.src = "https://via.placeholder.com/44"; }}
                      />
                      <div className="od-item-info">
                        <p className="od-item-name">{item.name}</p>
                        <div className="od-item-qty">
                          <span className="od-qty-badge">×{item.qty}</span>
                          <span>₹{item.price} each</span>
                        </div>
                      </div>
                      <span className="od-item-subtotal">₹{item.subtotal ?? item.price * item.qty}</span>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="od-divider" />
                <div className="od-card-footer">
                  <div className="od-total-text">
                    Total
                    <span className="od-total-amount">₹{order.totalPrice?.toFixed(2)}</span>
                  </div>
                  <div className="od-actions">
                    {!["served", "cancelled"].includes(order.status) && (
                      <button
                        className="od-cancel-btn"
                        disabled={busy}
                        onClick={() => updateStatus(order.orderId, "cancelled")}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M18 6 6 18M6 6l12 12"/>
                        </svg>
                        Cancel
                      </button>
                    )}
                    {st.next && (
                      <button
                        className="od-next-btn"
                        disabled={busy}
                        onClick={() => updateStatus(order.orderId, st.next)}
                        style={{ background: STATUS[st.next]?.dot }}
                      >
                        {busy ? (
                          "Updating…"
                        ) : (
                          <>
                            {STATUS[st.next]?.emoji}
                            Mark as {STATUS[st.next]?.label}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {isNew && <div className="od-new-badge">🆕 New Order</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}